/* eslint-disable no-unused-vars */
/* eslint-disable react/prop-types */
import React, { useContext } from "react";
import { useTranslation } from "react-i18next";
import { CareGiverContext } from "../context/CareGiverContext";
import {
  X,
  MapPin,
  Clock,
  Check,
  ArrowRight,
  Calendar,
  Home,
  Baby,
  BookOpen,
  Heart,
  ChefHat,
  Sparkles,
  AlertCircle,
  Users,
} from "lucide-react";
import {
  formatWorkHours,
  formatDays,
  formatExperienceDisplay,
  formatLocation,
  getCategoryLabel,
  formatWorkType,
  formatSalary,
  formatShortDate,
} from "../utils/formatters";

const JobDetailModal = ({ job, isOpen, onClose, onApply, hasActiveWork }) => {
  const { t } = useTranslation();
  const { getWorkStateLabel, getWorkStateColor } =
    useContext(CareGiverContext) || {};

  if (!isOpen || !job) return null;

  const renderCategorySpecificDetails = () => {
    switch (job.category) {
      case "childcare":
        return (
          <div className="bg-primary/10 dark:bg-primary/20 rounded-2xl p-5 mb-6">
            <h4 className="font-bold text-primary dark:text-primary mb-4 flex items-center gap-2">
              <Baby size={20} />
              {t("modals.jobDetail.childCareDetails")}
            </h4>
            {job.age_groups && job.age_groups.length > 0 && (
              <div className="mb-3">
                <p className="text-sm text-primary/80 dark:text-primary/70 mb-2">
                  {t("modals.jobDetail.agesCaredFor")}
                </p>
                <div className="flex flex-wrap gap-2">
                  {job.age_groups.map((age, index) => (
                    <span
                      key={index}
                      className="px-3 py-1.5 bg-card dark:bg-dark-surface text-primary dark:text-primary rounded-full text-sm font-medium shadow-sm"
                    >
                      {age.replace(/_/g, " ")}
                    </span>
                  ))}
                </div>
              </div>
            )}
          </div>
        );

      case "cook":
        return (
          <div className="bg-secondary/10 dark:bg-secondary/20 rounded-2xl p-5 mb-6">
            <h4 className="font-bold text-secondary dark:text-secondary mb-4 flex items-center gap-2">
              <ChefHat size={20} />
              {t("modals.jobDetail.cookingRequirements")}
            </h4>
            <div className="space-y-3">
              {job.family_size && (
                <div className="flex items-center gap-2 text-secondary/90 dark:text-secondary/80">
                  <Users size={16} />
                  <span>
                    {t("modals.jobDetail.familySize")}{" "}
                    <strong className="capitalize">{job.family_size}</strong>
                  </span>
                </div>
              )}
              {job.service_frequency && (
                <div className="flex items-center gap-2 text-secondary/90 dark:text-secondary/80">
                  <Calendar size={16} />
                  <span>
                    <strong>{job.service_frequency}</strong>{" "}
                    {t("modals.jobDetail.daysPerWeek")}
                  </span>
                </div>
              )}
              {job.meal_preparation === "Yes" && (
                <div className="flex items-center gap-2 text-secondary/90 dark:text-secondary/80">
                  <Check size={16} className="text-primary" />
                  <span>{t("modals.jobDetail.mealPrepNeeded")}</span>
                </div>
              )}
            </div>
          </div>
        );

      case "cleaner":
        return (
          <div className="bg-primary/10 dark:bg-primary/20 rounded-2xl p-5 mb-6">
            <h4 className="font-bold text-primary dark:text-primary mb-4 flex items-center gap-2">
              <Sparkles size={20} />
              {t("modals.jobDetail.cleaningDetails")}
            </h4>
            <div className="space-y-3">
              {job.family_size && (
                <div className="flex items-center gap-2 text-primary/90 dark:text-primary/80">
                  <Users size={16} />
                  <span>
                    {t("modals.jobDetail.homeSize")}{" "}
                    <strong className="capitalize">{job.family_size}</strong>
                  </span>
                </div>
              )}
              {job.room_numbers && (
                <div className="flex items-center gap-2 text-primary/90 dark:text-primary/80">
                  <Home size={16} />
                  <span>
                    <strong>{job.room_numbers}</strong>{" "}
                    {t("modals.jobDetail.roomsToClean")}
                  </span>
                </div>
              )}
              {job.light_housekeeping === "Yes" && (
                <div className="flex items-center gap-2 text-primary/90 dark:text-primary/80">
                  <Check size={16} className="text-primary" />
                  <span>{t("modals.jobDetail.lightHousekeepingIncluded")}</span>
                </div>
              )}
            </div>
          </div>
        );

      case "homeTutor":
        return (
          <div className="bg-secondary/10 dark:bg-secondary/20 rounded-2xl p-5 mb-6">
            <h4 className="font-bold text-secondary dark:text-secondary mb-4 flex items-center gap-2">
              <BookOpen size={20} />
              {t("modals.jobDetail.tutoringInformation")}
            </h4>
            {job.teaching_levels && job.teaching_levels.length > 0 && (
              <div className="mb-3">
                <p className="text-sm text-secondary/80 dark:text-secondary/70 mb-2">
                  {t("modals.jobDetail.studentLevels")}
                </p>
                <div className="flex flex-wrap gap-2">
                  {job.teaching_levels.map((level, index) => (
                    <span
                      key={index}
                      className="px-3 py-1.5 bg-card dark:bg-dark-surface text-secondary dark:text-secondary rounded-full text-sm font-medium shadow-sm"
                    >
                      {level}
                    </span>
                  ))}
                </div>
              </div>
            )}
            {job.hours_per_day && (
              <div className="flex items-center gap-2 text-secondary/90 dark:text-secondary/80 mt-3">
                <Clock size={16} />
                <span>
                  <strong>{job.hours_per_day}</strong>{" "}
                  {t("modals.jobDetail.hoursPerDay")}
                </span>
              </div>
            )}
            {job.preferred_days && job.preferred_days.length > 0 && (
              <div className="flex items-center gap-2 text-secondary/90 dark:text-secondary/80 mt-2">
                <Calendar size={16} />
                <span>
                  {t("modals.jobDetail.daysLabel")}{" "}
                  <strong>{job.preferred_days.join(", ")}</strong>
                </span>
              </div>
            )}
          </div>
        );

      case "elderlycare":
        return (
          <div className="bg-primary/10 dark:bg-primary/20 rounded-2xl p-5 mb-6">
            <h4 className="font-bold text-primary dark:text-primary mb-4 flex items-center gap-2">
              <Heart size={20} />
              {t("modals.jobDetail.careServicesNeeded")}
            </h4>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              {job.meal_preparation === "Yes" && (
                <div className="flex items-center gap-2 text-primary/90 dark:text-primary/80 bg-card dark:bg-dark-surface p-2 rounded-lg">
                  <Check size={16} className="text-primary" />
                  <span className="text-sm">
                    {t("modals.jobDetail.mealPreparation")}
                  </span>
                </div>
              )}
              {job.light_housekeeping === "Yes" && (
                <div className="flex items-center gap-2 text-primary/90 dark:text-primary/80 bg-card dark:bg-dark-surface p-2 rounded-lg">
                  <Check size={16} className="text-primary" />
                  <span className="text-sm">
                    {t("modals.jobDetail.lightHousekeeping")}
                  </span>
                </div>
              )}
              {job.medication_management === "Yes" && (
                <div className="flex items-center gap-2 text-primary/90 dark:text-primary/80 bg-card dark:bg-dark-surface p-2 rounded-lg">
                  <Check size={16} className="text-primary" />
                  <span className="text-sm">
                    {t("modals.jobDetail.medicationHelp")}
                  </span>
                </div>
              )}
              {job.mobility_assistance === "Yes" && (
                <div className="flex items-center gap-2 text-primary/90 dark:text-primary/80 bg-card dark:bg-dark-surface p-2 rounded-lg">
                  <Check size={16} className="text-primary" />
                  <span className="text-sm">
                    {t("modals.jobDetail.mobilityAssistance")}
                  </span>
                </div>
              )}
              {job.companionship === "Yes" && (
                <div className="flex items-center gap-2 text-primary/90 dark:text-primary/80 bg-card dark:bg-dark-surface p-2 rounded-lg">
                  <Check size={16} className="text-primary" />
                  <span className="text-sm">
                    {t("modals.jobDetail.companionship")}
                  </span>
                </div>
              )}
              {job.transportation_assistance === "Yes" && (
                <div className="flex items-center gap-2 text-primary/90 dark:text-primary/80 bg-card dark:bg-dark-surface p-2 rounded-lg">
                  <Check size={16} className="text-primary" />
                  <span className="text-sm">
                    {t("modals.jobDetail.transportation")}
                  </span>
                </div>
              )}
            </div>
            {job.first_aid_cpr_certified === "Yes" && (
              <div className="mt-4 flex items-center gap-2 text-primary/90 dark:text-primary/80 text-sm font-medium">
                <AlertCircle size={16} />
                {t("modals.jobDetail.firstAidRequired")}
              </div>
            )}
          </div>
        );

      default:
        return null;
    }
  };

  const formattedHours = formatWorkHours(job.preferred_work_hours);

  return (
    <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center p-4 z-50 animate-in fade-in duration-200">
      <div className="bg-card dark:bg-dark-surface rounded-3xl w-full max-w-2xl max-h-[90vh] overflow-y-auto shadow-2xl animate-in zoom-in-95 duration-300">
        {/* Header */}
        <div className="sticky top-0 bg-card dark:bg-dark-surface border-b border-accent dark:border-dark-border p-6 rounded-t-3xl flex justify-between items-start z-10">
          <div className="flex-1 pr-4">
            <span className="inline-block px-3 py-1 bg-primary text-white rounded-full text-xs font-bold mb-2">
              {getCategoryLabel(job.category)}
            </span>
            <h2 className="text-2xl font-bold text-text dark:text-dark-text leading-tight">
              {job.description
                ? job.description.substring(0, 80) +
                  (job.description.length > 80 ? "..." : "")
                : t("modals.jobDetail.jobOpportunity")}
            </h2>
            <div className="flex items-center gap-2 mt-3 text-sm">
              <MapPin size={16} className="text-text/60" />
              <span className="text-text/70 dark:text-dark-muted">
                {formatLocation(job.subcity, job.location)}
              </span>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-2 hover:bg-accent dark:hover:bg-dark-primary rounded-full transition-colors shrink-0"
          >
            <X size={24} className="text-text/60" />
          </button>
        </div>

        {/* Content */}
        <div className="p-6">
          {/* Key Info Cards */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
            <div className="bg-background dark:bg-dark-surface rounded-2xl p-4 text-center">
              <p className="text-xs text-text/60 dark:text-dark-muted uppercase tracking-wide">
                {t("modals.jobDetail.experience")}
              </p>
              <p className="font-bold text-text dark:text-dark-text text-sm">
                {formatExperienceDisplay(job.experienceLevel)}{" "}
                {job.experienceLevel && "years"}
              </p>
            </div>

            <div className="bg-background dark:bg-dark-surface rounded-2xl p-4 text-center">
              <p className="text-xs text-text/60 dark:text-dark-muted uppercase tracking-wide">
                {t("modals.jobDetail.workType")}
              </p>
              <p className="font-bold text-text dark:text-dark-text text-sm">
                {formatWorkType(job.workTime)}
              </p>
            </div>

            <div className="bg-background dark:bg-dark-surface rounded-2xl p-4 text-center">
              <p className="text-xs text-text/60 dark:text-dark-muted uppercase tracking-wide">
                {t("modals.jobDetail.payment")}
              </p>
              <p className="font-bold text-text dark:text-dark-text">
                {formatSalary(job.salary)}
              </p>
            </div>

            <div className="bg-background dark:bg-dark-surface rounded-2xl p-4 text-center">
              <p className="text-xs text-text/60 dark:text-dark-muted uppercase tracking-wide">
                {t("modals.jobDetail.posted")}
              </p>
              <p className="font-bold text-text dark:text-dark-text text-sm">
                {formatShortDate(job.createdAt)}
              </p>
            </div>
          </div>

          {/* Full Description */}
          <div className="mb-8">
            <h3 className="text-lg font-bold text-text dark:text-dark-text mb-3 flex items-center gap-2">
              {t("modals.jobDetail.aboutJob")}
            </h3>
            <div className="bg-background dark:bg-dark-surface rounded-2xl p-5">
              <p className="text-text/80 dark:text-dark-text/80 leading-relaxed">
                {job.description || t("modals.jobDetail.noDescription")}
              </p>
            </div>
          </div>

          {/* Category Specific Section */}
          {renderCategorySpecificDetails()}

          {/* Schedule Info */}
          {(formattedHours ||
            (job.preferred_days && job.preferred_days.length > 0)) && (
            <div className="mb-6">
              <h4 className="font-bold text-text dark:text-dark-text mb-3 flex items-center gap-2">
                {t("modals.jobDetail.preferredSchedule")}
              </h4>
              <div className="flex flex-wrap gap-2">
                {formattedHours &&
                  formattedHours.map((hour, idx) => (
                    <span
                      key={idx}
                      className="px-4 py-2 bg-accent dark:bg-dark-border/30 text-text dark:text-dark-text rounded-full text-sm font-medium"
                    >
                      {hour}
                    </span>
                  ))}
                {job.preferred_days &&
                  formatDays(job.preferred_days)?.map((day, idx) => (
                    <span
                      key={idx}
                      className="px-3 py-2 bg-accent dark:bg-dark-border text-text dark:text-dark-text rounded-lg text-sm font-medium"
                    >
                      {day}
                    </span>
                  ))}
              </div>
            </div>
          )}

          {/* Languages */}
          {job.languages && job.languages.length > 0 && (
            <div className="mb-6">
              <h4 className="font-bold text-text dark:text-dark-text mb-3 flex items-center gap-2">
                {t("modals.jobDetail.languagesNeeded")}
              </h4>
              <div className="flex flex-wrap gap-2">
                {job.languages.map((language, index) => (
                  <span
                    key={index}
                    className="px-4 py-2 bg-primary text-white rounded-full text-sm font-medium"
                  >
                    {language}
                  </span>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="sticky bottom-0 bg-card dark:bg-dark-surface border-t border-accent dark:border-dark-border p-6 rounded-b-3xl">
          <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
            <div className="space-y-1">
              {/* Status Badges */}
              <div className="flex items-center gap-2 flex-wrap">
                <span
                  className={`px-3 py-1 rounded-full text-xs font-bold text-white ${
                    job.status === "open"
                      ? "bg-primary"
                      : "bg-slate-500"
                  }`}
                >
                  {job.status === "open"
                    ? t("modals.jobDetail.acceptingApplications")
                    : t("modals.jobDetail.closed")}
                </span>

                {job.workState && job.workState !== "pending" && (
                  <span
                    className={`px-3 py-1 rounded-full text-xs font-bold ${getWorkStateColor ? getWorkStateColor(job.workState) : "bg-accent text-text"}`}
                  >
                    {getWorkStateLabel
                      ? getWorkStateLabel(job.workState)
                      : job.workState}
                  </span>
                )}
              </div>
            </div>

            <button
              onClick={() => {
                onApply(job.id);
                onClose();
              }}
              disabled={hasActiveWork || job.workState !== "pending"}
              className={`w-full sm:w-auto px-8 py-3 rounded-xl font-bold transition-all flex items-center justify-center gap-2 shadow-lg ${
                hasActiveWork || job.workState !== "pending"
                  ? "bg-accent text-text/50 cursor-not-allowed shadow-none"
                  : "bg-primary hover:bg-primary/90 text-white shadow-primary/20 hover:scale-105"
              }`}
            >
              {hasActiveWork ? (
                t("modals.jobDetail.currentlyWorking")
              ) : job.workState !== "pending" ? (
                t("modals.jobDetail.noLongerAvailable")
              ) : (
                <>
                  {t("modals.jobDetail.applyJob")} <ArrowRight size={20} />
                </>
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default JobDetailModal;
